import { useEffect } from 'react';
import { absoluteUrl, SEO } from '@/lib/seo';

type SeoProps = {
  title?: string;
  description?: string;
  /** Path for canonical / og:url, e.g. `/login` */
  path?: string;
  /** When true, ask crawlers not to index (authenticated or transient pages). */
  noIndex?: boolean;
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Sets document title and key meta tags for the current route.
 * Complements the static tags in index.html for client-side navigation.
 */
export function Seo({
  title = SEO.defaultTitle,
  description = SEO.defaultDescription,
  path,
  noIndex = false,
}: SeoProps) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    if (path) {
      const url = absoluteUrl(path);
      upsertLink('canonical', url);
      upsertMeta('property', 'og:url', url);
    }

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, path, noIndex]);

  return null;
}
